import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { EmptyState } from '../../../components/v2';
import BaseService from '../../../services/BaseService';
import * as actionsRelatorioJob from '../../../store/relatorioJob/actions';
import * as selectorsRelatorioJob from '../../../store/relatorioJob/reducer';
import {
  RELATORIO_JOB_PAGE_SIZE,
  RELATORIO_JOB_POLL_MS,
  getJobSubtitle,
  isRelatorioJobAvailable,
  isRelatorioJobInFlight,
  parseRelatorioJobsPage,
} from '../../../helpers/relatorioJob';
import RelatorioJobStatusIcon from './RelatorioJobStatusIcon';
import RelatorioJobDownloadButton from './RelatorioJobDownloadButton';

const formatWhen = (value) => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '';
};

const DownloadsV2 = ({ downloadRelatorioJob, downloadingId }) => {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await BaseService.get({
        url: `reports/jobs?p=${page}&s=${RELATORIO_JOB_PAGE_SIZE}`,
      });
      const parsed = parseRelatorioJobsPage(response.data);
      setRows(parsed.items);
      setTotal(parsed.totalElements);
    } catch (error) {
      if (!silent) {
        setRows([]);
        setTotal(0);
      }
    }
    if (!silent) setLoading(false);
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const shouldPoll = rows.some((job) => isRelatorioJobInFlight(job.status));
    if (!shouldPoll) return undefined;
    const timer = window.setInterval(() => {
      load(true);
    }, RELATORIO_JOB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [rows, load]);

  return (
    <Box>
      <Stack spacing={3}>
        <Typography variant="h4">Downloads</Typography>
        {rows.length === 0 && !loading && (
          <EmptyState title="Nenhum relatório gerado" message="Os arquivos que você solicitar aparecerão aqui." />
        )}
        {rows.map((job) => (
          <Box
            key={job.id}
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
              py: 1.5,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <RelatorioJobStatusIcon status={job.status} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                {job.titulo}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {getJobSubtitle(job)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatWhen(job.dataCriacao)}
              </Typography>
            </Box>
            {isRelatorioJobAvailable(job.status) && (
              <RelatorioJobDownloadButton
                job={job}
                downloadingId={downloadingId}
                onDownload={downloadRelatorioJob}
              />
            )}
          </Box>
        ))}
        {total > RELATORIO_JOB_PAGE_SIZE && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={RELATORIO_JOB_PAGE_SIZE}
            rowsPerPageOptions={[RELATORIO_JOB_PAGE_SIZE]}
            labelRowsPerPage=""
          />
        )}
      </Stack>
    </Box>
  );
};

DownloadsV2.propTypes = {
  downloadRelatorioJob: PropTypes.func.isRequired,
  downloadingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DownloadsV2.defaultProps = {
  downloadingId: null,
};

const mapStateToProps = (state) => ({
  downloadingId: selectorsRelatorioJob.getRelatorioJobDownloadingId(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsRelatorioJob }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(DownloadsV2);
